import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Select, Table, Space, Row, Col, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useGetQaqcPipeWeldingdataLog } from '../../hooks/genericHooks';
import type { QaqcPipeWeldingdataLog } from '../../types';
import { fetchSqlDataWithQuery } from '../../services/api';
 

export interface WeldSelectValue { weldingdata_id: string; weld_no_rev: number | string | null; }

interface Props {
  open: boolean;
  title?: string;
  onCancel: () => void;
  onOk: (items: any[]) => void;
  value?: any[];
  mode?: 'add' | 'remove';
  outputMode?: 'basic' | 'full';
  returnAllModes?: boolean;
}

const norm = (x: any) => (x == null ? '' : String(x)).trim().toLowerCase();

const keyFrom = (w: { weldingdata_id: string; weld_no_rev: number | string | null }) => `${String(w.weldingdata_id)}|${String(w.weld_no_rev)}`;

const WeldSelectModal: React.FC<Props> = ({ open, title, onCancel, onOk, value, mode = 'add', outputMode = 'basic', returnAllModes = false }) => {
  const { data: logResp, isLoading: loadingLog, isFetching: fetchingLog } = useGetQaqcPipeWeldingdataLog() as any;
  const weldingLogList: QaqcPipeWeldingdataLog[] = (logResp as any)?.data ?? (logResp as any) ?? [];

  // Filters copied from QaqcPipeWeldingDataResultList.tsx
  const [filters, setFilters] = useState<{
    fluid?: string[];
    section?: string[];
    line_no?: string[];
    spool_id?: string[];
    revision_no?: string[];
    serviceclass_name?: string[];
  }>({});

  const cascadeOrder: (keyof QaqcPipeWeldingdataLog)[] = [
    'fluid',
    'section',
    'line_no',
    'spool_id',
    'revision_no',
    'serviceclass_name',
  ];

  const applyLogFilters = (
    dataIn: QaqcPipeWeldingdataLog[],
    opts?: { excludeField?: keyof QaqcPipeWeldingdataLog }
  ) => {
    const hasFluid = !!(filters as any).fluid?.length;
    const hasSection = !!(filters as any).section?.length;
    const hasLine = !!(filters as any).line_no?.length;
    const hasSpool = !!(filters as any).spool_id?.length;
    const hasRev = !!(filters as any).revision_no?.length;
    const hasSvc = !!(filters as any).serviceclass_name?.length;
    const onlySectionActive = hasSection && !hasFluid && !hasLine && !hasSpool && !hasRev && !hasSvc;

    if (onlySectionActive && opts?.excludeField !== 'section') {
      const sectVals = (filters as any).section as string[];
      return dataIn.filter(row => {
        const wid = String((row as any).weldingdata_id ?? '').toLowerCase();
        return sectVals.some(v => wid.includes(String(v).toLowerCase()));
      });
    }

    return dataIn.filter(row => {
      for (const k of cascadeOrder) {
        if (opts?.excludeField === k) continue;
        const vals = (filters as any)[k] as string[] | undefined;
        if (vals && vals.length > 0) {
          const cell = String((row as any)[k] ?? '').toLowerCase();
          const matched = k === 'line_no'
            ? vals.some(v => String(v).toLowerCase().trim() !== '' && cell.includes(String(v).toLowerCase()))
            : vals.some(v => String(v).toLowerCase() === cell);
          if (!matched) return false;
        }
      }
      return true;
    });
  };

  const buildWeldingLogOptions = (field: keyof QaqcPipeWeldingdataLog) => {
    const base = applyLogFilters(weldingLogList, { excludeField: field });
    const set = new Set<string>();
    base.forEach(r => {
      const v = (r as any)[field];
      if (v !== null && v !== undefined && String(v).trim() !== '') set.add(String(v));
    });
    return Array.from(set).sort((a,b) => a.localeCompare(b)).map(v => ({ label: v, value: v }));
  };

  // Compute weldingdata_id set based on filters
  const weldingIdKey = useMemo(() => {
    const base = applyLogFilters(weldingLogList);
    const ids = new Set<string>();
    base.forEach(r => { if ((r as any).weldingdata_id) ids.add(String((r as any).weldingdata_id)); });
    return Array.from(ids).sort().join(',');
  }, [weldingLogList, filters]);

  const filteredWeldingDataIds = useMemo(() => new Set<string>(weldingIdKey ? weldingIdKey.split(',') : []), [weldingIdKey]);

  // Fetch distinct (weldingdata_id, weld_no_rev)
  type PairRow = { key: string; weldingdata_id: string; weld_no_rev: number | string | null } & Record<string, any>;
  const [pairs, setPairs] = useState<PairRow[]>([]);
  const [loadingPairs, setLoadingPairs] = useState(false);

  useEffect(() => {
    if (mode !== 'add') return; // only fetch in add mode
    let cancelled = false;
    const run = async () => {
      setLoadingPairs(true);
      try {
        // Build distinct pairs directly from weldingLogList to avoid empty list on first open
        // Do not exclude invalidity flags here so we can disable selection for invalid rows
        const exclude = new Set(['mod_date','line_no','section','weld_no','remarks','id','created_at','created_by','updated_at','updated_by','project_id']);
        const pairsMap = new Map<string, PairRow>();
        const allowIds = filteredWeldingDataIds && filteredWeldingDataIds.size > 0 ? filteredWeldingDataIds : null;
        (weldingLogList || []).forEach((lr: any) => {
          const id = lr?.weldingdata_id != null ? String(lr.weldingdata_id) : undefined;
          if (!id) return;
          if (allowIds && !allowIds.has(id)) return;
          const rev = (lr.weld_no_rev ?? lr.revision_no ?? null) as any;
          const key = `${id}|${String(rev)}`;
          if (pairsMap.has(key)) return;
          const base: PairRow = { key, weldingdata_id: id, weld_no_rev: rev };
          Object.keys(lr).forEach((k) => {
            if (!exclude.has(k) && k !== 'weldingdata_id' && k !== 'weld_no_rev') {
              (base as any)[k] = (lr as any)[k];
            }
          });
          pairsMap.set(key, base);
        });
        const mapped: PairRow[] = Array.from(pairsMap.values()).sort((a,b)=>{
          if (a.weldingdata_id === b.weldingdata_id) return String(a.weld_no_rev ?? '').localeCompare(String(b.weld_no_rev ?? ''));
          return String(a.weldingdata_id).localeCompare(String(b.weldingdata_id));
        });
        if (!cancelled) setPairs(mapped);
      } catch {
        if (!cancelled) setPairs([]);
      } finally {
        if (!cancelled) setLoadingPairs(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [filteredWeldingDataIds, filters, mode]);

  // Selection and pagination
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [pageSize, setPageSize] = useState<number>(20);
  const [current, setCurrent] = useState<number>(1);
  useEffect(() => { setCurrent(1); }, [filters]);

  // Initialize/Sync selection from parent value when modal opens or value changes
  useEffect(() => {
    if (!open) return;
    const keys = Array.isArray(value) ? value.map((v: any) => keyFrom({ weldingdata_id: (v as any).weldingdata_id, weld_no_rev: (v as any).weld_no_rev })) : [];
    setSelectedRowKeys(keys);
  }, [open, value]);

  // Helper to build the final selection list based on selectedRowKeys, current pairs, and initial value
  const buildSelectedItems = (keys: React.Key[]): any[] => {
    const existingMap = new Map<string, WeldSelectValue>();
    (value || []).forEach((v: any) => existingMap.set(keyFrom({ weldingdata_id: v.weldingdata_id, weld_no_rev: v.weld_no_rev }), v));
    const pairMap = new Map<string, WeldSelectValue>();
    const fullMap = new Map<string, any>();
    const auditExclude = new Set(['created_at','updated_at','created_by','updated_by']);
    pairs.forEach(p => {
      pairMap.set(p.key, { weldingdata_id: p.weldingdata_id, weld_no_rev: p.weld_no_rev });
      const full: any = {};
      Object.keys(p).forEach(k => { if (!auditExclude.has(k)) (full as any)[k] = (p as any)[k]; });
      fullMap.set(p.key, full);
    });
    const out: any[] = [];
    keys.forEach(k => {
      const kk = String(k);
      let item: any;
      if (outputMode === 'full') {
        item = fullMap.get(kk) || existingMap.get(kk) || fullMap.get(kk);
      } else {
        item = pairMap.get(kk) || existingMap.get(kk);
      }
      if (item) out.push(item);
    });
    return out;
  };

  const buildSelectedAllModes = (keys: React.Key[]) => {
    const pairMap = new Map<string, WeldSelectValue>();
    const fullMap = new Map<string, any>();
    const auditExclude = new Set(['created_at','updated_at','created_by','updated_by']);
    pairs.forEach(p => {
      pairMap.set(p.key, { weldingdata_id: p.weldingdata_id, weld_no_rev: p.weld_no_rev });
      const full: any = {};
      Object.keys(p).forEach(k => { if (!auditExclude.has(k)) (full as any)[k] = (p as any)[k]; });
      fullMap.set(p.key, full);
    });
    const basic: WeldSelectValue[] = [];
    const full: any[] = [];
    keys.forEach(k => {
      const kk = String(k);
      const b = pairMap.get(kk);
      const f = fullMap.get(kk);
      if (b) basic.push(b);
      if (f) full.push(f);
    });
    return { type: 'welds', modes: { basic, full } } as any;
  };

  const effectivePairs: PairRow[] = useMemo(() => {
    if (mode === 'remove') {
      const src = Array.isArray(value) ? value : [];
      return src.map(v => ({ key: keyFrom(v), weldingdata_id: v.weldingdata_id, weld_no_rev: v.weld_no_rev }));
    }
    return pairs;
  }, [mode, value, pairs]);

  const duplicateIdSet = useMemo(() => {
    const counts = new Map<string, number>();
    (effectivePairs || []).forEach(r => {
      const id = String(r.weldingdata_id ?? '');
      counts.set(id, (counts.get(id) || 0) + 1);
    });
    const dups = new Set<string>();
    counts.forEach((cnt, id) => { if (cnt > 1) dups.add(id); });
    return dups;
  }, [effectivePairs]);

  const columns: ColumnsType<PairRow> = React.useMemo(() => {
    const exclude = new Set(['mod_date','line_no','section','fluid','weld_no','remarks','id','created_at','created_by','updated_at','updated_by','project_id','invalidity','is_invalid','invalid','revision_no']);
    const base: ColumnsType<PairRow> = [
      { title: 'Welding ID', dataIndex: 'weldingdata_id', key: 'weldingdata_id', width: 220, ellipsis: true },
      { title: 'W.rev', dataIndex: 'weld_no_rev', key: 'weld_no_rev', width: 50, ellipsis: true },
    ];
    const sample = (effectivePairs && effectivePairs.length > 0) ? effectivePairs[0] : undefined;
    if (sample) {
      Object.keys(sample).forEach((k) => {
        if (k === 'key' || k === 'weldingdata_id' || k === 'weld_no_rev') return;
        if (exclude.has(k)) return;
        // Build compact header: strip trailing _name, map to short labels
        const shortMap: Record<string, string> = {
          serviceclass_name: 'SC',
          serviceclass: 'SC',
          weld_category: 'S/F',
          weld_category_name: 'S/F',
          weldtype_name: 'Type',
          weldtype: 'Type',
          pipesize_name: 'P.Size',
          pipesize: 'P.Size',
          spool_id: 'Spool',
          revision_no: 'W.rev',
          code_no: 'Code',
        };
        let headerKey = k;
        if (headerKey.endsWith('_name')) headerKey = headerKey.slice(0, -5);
        const title = shortMap[k] || shortMap[headerKey] || headerKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        base.push({ title, dataIndex: k as any, key: k, width: 40, ellipsis: true });
      });
    }
    return base;
  }, [effectivePairs]);

  return (
    <Modal
      open={open}
      title={title || 'Select Welds'}
      onCancel={onCancel}
      onOk={() => {
        if (mode === 'remove' && selectedRowKeys.length === 0 && Array.isArray(value)) {
          onOk(value);
          return;
        }
        if (returnAllModes) {
          const payload = buildSelectedAllModes(selectedRowKeys);
          onOk(payload as any);
        } else {
          const items = buildSelectedItems(selectedRowKeys);
          onOk(items);
        }
      }}
      width={640}
      bodyStyle={{ overflowY: 'visible' }}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <style>{`
          .weld-table .ant-table-tbody > tr > td,
          .weld-table .ant-table-thead > tr > th { padding-top: 4px !important; padding-bottom: 4px !important; }
          .weld-table .ant-table-cell { white-space: nowrap; }
          /* Smaller selection checkbox */
          .weld-table .ant-table-selection-column { width: 16px !important; min-width: 16px !important; padding-left: 0 !important; padding-right: 0 !important; }
          .weld-table .ant-table-selection-column .ant-checkbox .ant-checkbox-inner { width: 14px; height: 14px; }
          .weld-table .ant-table-selection-column .ant-checkbox { line-height: 10px; }
          .weld-table .ant-table-selection-column .ant-checkbox-wrapper { line-height: 10px; }
          /* Invalid row highlight */
          .weld-table .row-invalid td { background-color: #ffd6e7 !important; }
          /* Duplicate id highlight (non-invalid only) */
          .weld-table .row-dup td { background-color: #fff7b8 !important; }
        `}</style>
        {mode === 'add' && (
        <Row gutter={[8,8]}>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Select mode="multiple" allowClear showSearch options={buildWeldingLogOptions('fluid')} placeholder="Fluid" value={filters.fluid}
                    onChange={(v) => setFilters(prev => ({ ...prev, fluid: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Select mode="multiple" allowClear showSearch options={buildWeldingLogOptions('section')} placeholder="Area (Section)" value={filters.section}
                    onChange={(v) => setFilters(prev => ({ ...prev, section: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <Select mode="multiple" allowClear showSearch options={buildWeldingLogOptions('line_no')} placeholder="Line No" value={filters.line_no}
                    onChange={(v) => setFilters(prev => ({ ...prev, line_no: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Select mode="multiple" allowClear showSearch options={buildWeldingLogOptions('spool_id')} placeholder="Spool" value={filters.spool_id}
                    onChange={(v) => setFilters(prev => ({ ...prev, spool_id: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={3}>
            <Select mode="multiple" allowClear showSearch options={buildWeldingLogOptions('revision_no')} placeholder="Rev" value={filters.revision_no}
                    onChange={(v) => setFilters(prev => ({ ...prev, revision_no: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={3}>
            <Select mode="multiple" allowClear showSearch options={buildWeldingLogOptions('serviceclass_name')} placeholder="S.Class" value={filters.serviceclass_name}
                    onChange={(v) => setFilters(prev => ({ ...prev, serviceclass_name: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <Button size="small" onClick={() => { setFilters({}); setCurrent(1); }}>Reset Filters</Button>
          </Col>
        </Row>
        )}
        <Table<PairRow>
          className="weld-table"
          size="small"
          rowKey={(r) => r.key}
          columns={columns}
          dataSource={effectivePairs}
          scroll={{ x: 'max-content' }}
          tableLayout="fixed"
          loading={mode === 'add' ? (loadingPairs || loadingLog || fetchingLog) : false}
          pagination={{
            current,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10','20','50','100'],
            showTotal: (total, range) => `${total} row(s)`,
            onChange: (p, ps) => { setCurrent(p); setPageSize(ps); },
          }}
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys as React.Key[]),
            columnWidth: 16,
            preserveSelectedRowKeys: true,
            getCheckboxProps: (record) => {
              const v = (record as any).invalidity ?? (record as any).is_invalid ?? (record as any).invalid;
              const inv = v === true || v === 1 || v === '1';
              return { disabled: inv, title: inv ? 'Invalid' : undefined } as any;
            },
          }}
          rowClassName={(record) => {
            const v = (record as any).invalidity ?? (record as any).is_invalid ?? (record as any).invalid;
            const inv = v === true || v === 1 || v === '1';
            if (inv) return 'row-invalid';
            const isDup = duplicateIdSet.has(String((record as any).weldingdata_id ?? ''));
            // highlight dup only when invalidity is explicitly 0
            const v2 = (record as any).invalidity ?? (record as any).is_invalid ?? (record as any).invalid;
            const isZero = v2 === 0 || v2 === '0' || v2 === false || v2 === null || v2 === undefined ? (v2 === 0 || v2 === '0') : false;
            return isDup && isZero ? 'row-dup' : '';
          }}
          onRow={(record) => ({
            onDoubleClick: () => {
              const k = record.key;
              const v = (record as any).invalidity ?? (record as any).is_invalid ?? (record as any).invalid;
              const inv = v === true || v === 1 || v === '1';
              if (inv) return;
              const exists = selectedRowKeys.includes(k);
              const newKeys = exists
                ? selectedRowKeys.filter(x => x !== k)
                : [...selectedRowKeys, k];
              setSelectedRowKeys(newKeys);
              if (returnAllModes) {
                const payload = buildSelectedAllModes(newKeys);
                onOk(payload as any);
              } else {
                const items = buildSelectedItems(newKeys);
                onOk(items);
              }
            }
          })}
        />
      </Space>
    </Modal>
  );
};

export default WeldSelectModal;
